import React, { useEffect, useState } from 'react';
import { ArrowRight, Calendar, Tag, Loader2 } from 'lucide-react';

const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const API_BASE_URL =
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:5000/api'
      : 'https://dibyanjaya-portfolio-backend.onrender.com/api';
  
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/blog`);
        
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        
        const data = await response.json();
        setPosts(data.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts');
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, [API_BASE_URL]);
  
  if (error) {
    return (
      <div className="flex items-center justify-center p-8 rounded-lg bg-red-50 text-red-500 text-lg font-medium">
        <span>{error}</span>
      </div>
    );
  }
  
  return (
    <section className="py-16 px-4 md:px-8 max-w-7xl mx-auto">
      <div className="mb-12 text-center">
        <h2 className="text-sm font-semibold tracking-widest uppercase text-indigo-600 mb-2">From The Blog</h2>
        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Latest Articles</h1>
        <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">Explore insights, tutorials, and updates from our team</p>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-12 w-12 text-indigo-600 animate-spin" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <article 
              key={post.slug} 
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col h-full border border-gray-100"
            >
              <div className="h-48 bg-gradient-to-br from-indigo-100 to-purple-100 relative overflow-hidden">
                {post.coverImage ? (
                  <img 
                    src={post.coverImage} 
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <img 
                      src="/api/placeholder/400/320" 
                      alt="placeholder" 
                      className="opacity-50"
                    />
                  </div>
                )}
              </div>
                
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center text-sm text-gray-500 mb-3">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>{post.date || 'Recent'}</span>
                  <span className="mx-2">•</span>
                  <Tag className="h-4 w-4 mr-1" />
                  <span className="font-medium text-indigo-600">{post.category}</span>
                </div>
                
                <h2 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-indigo-600 transition-colors duration-200">
                  {post.title}
                </h2>
                
                <p className="text-gray-600 mb-4 flex-1">
                  {post.excerpt}
                </p>
                
                <a 
                  href={`/blog/${post.slug}`}
                  className="inline-flex items-center text-indigo-600 font-medium group-hover:text-indigo-700 mt-auto"
                >
                  Read more
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </article>
          ))}
        </div>
      )}
      
      {posts.length > 0 && (
        <div className="mt-12 text-center">
          <a 
            href="/blog" 
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-200"
          >
            View All Posts
          </a>
        </div>
      )}
    </section>
  );
};

export default BlogSection;